export type InvoiceStatus = 'DRAFT' | 'SENT' | 'VIEWED' | 'OVERDUE' | 'PAID' | 'PARTIALLY_PAID' | 'CANCELLED';
export type RecurrencePattern = 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY';

export interface Invoice {
  id: string;
  invoiceNumber: string;
  customerId: string;
  jobId?: string;
  templateId?: string;
  issueDate: string;
  dueDate: string;
  status: InvoiceStatus;
  subtotal: number;
  taxAmount: number;
  total: number;
  paidAmount: number;
  remainingAmount: number;
  notes?: string;
  isRecurring: boolean;
  recurrencePattern?: RecurrencePattern;
  recurrenceEndDate?: string;
  companyId: string;
  createdAt: string;
  updatedAt: string;
  customer?: InvoiceCustomer;
  job?: InvoiceJob;
  items?: InvoiceItem[];
  payments?: InvoicePayment[];
  company?: InvoiceCompany;
  template?: InvoiceTemplateData;
  _count?: {
    payments: number;
  };
}

export interface InvoiceJob {
  jobNumber: string;
  jobName: string;
}

export interface InvoiceCustomer {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

export interface InvoiceItem {
  id: string;
  invoiceId: string;
  itemId?: string;
  productId?: string;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
  taxRate?: number;
  taxAmount?: number;
  serialNumbers?: string[];
  item?: {
    id: string;
    name: string;
    unit: string;
    sellingPrice?: number;
  };
  product?: {
    id: string;
    name: string;
    sku: string;
    itemType: string;
    unitPrice: number;
    isSerialized: boolean;
  };
}

export interface InvoicePayment {
  id: string;
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  reference?: string;
  status: string;
  notes?: string;
  createdAt: string;
}

export interface InvoiceCompany {
  name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  logo?: string;
}

export interface CreateInvoiceData {
  customerId: string;
  jobId?: string;
  templateId: string;
  issueDate?: string;
  dueDate: string;
  notes?: string;
  isRecurring?: boolean;
  recurrencePattern?: RecurrencePattern;
  recurrenceEndDate?: string;
  items: CreateInvoiceItemData[];
}

export interface CreateInvoiceItemData {
  itemId?: string;
  productId?: string;
  description?: string;
  quantity: number;
  rate: number;
  taxRate?: number;
  serialNumbers?: string[];
}

export interface UpdateInvoiceData {
  customerId?: string;
  issueDate?: string;
  dueDate?: string;
  status?: InvoiceStatus;
  notes?: string;
  isRecurring?: boolean;
  recurrencePattern?: RecurrencePattern;
  recurrenceEndDate?: string;
  items?: CreateInvoiceItemData[];
}

export interface InvoiceStats {
  totalInvoices: number;
  draftInvoices: number;
  sentInvoices: number;
  paidInvoices: number;
  overdueInvoices: number;
  totalAmount: number;
  totalPaid: number;
  totalOutstanding: number;
}

export interface InvoicesResponse {
  invoices: Invoice[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

export interface InvoiceFilters {
  search?: string;
  status?: InvoiceStatus;
  customerId?: string;
  page?: number;
  limit?: number;
}

export interface InvoiceFormData {
  customerId: string;
  templateId: string;
  issueDate: string;
  dueDate: string;
  notes: string;
  isRecurring: boolean;
  recurrencePattern?: RecurrencePattern;
  recurrenceEndDate?: string;
  items: InvoiceItemFormData[];
}

export interface InvoiceItemFormData {
  productId?: string;
  description: string;
  quantity: string;
  rate: string;
  taxRate: string;
  amount: number;
  taxAmount: number;
  serialNumbers?: string[];
}

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  DRAFT: 'Draft',
  SENT: 'Sent',
  VIEWED: 'Viewed',
  OVERDUE: 'Overdue',
  PAID: 'Paid',
  PARTIALLY_PAID: 'Partially Paid',
  CANCELLED: 'Cancelled'
};

export const INVOICE_STATUS_COLORS: Record<InvoiceStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-800',
  SENT: 'bg-blue-100 text-blue-800',
  VIEWED: 'bg-yellow-100 text-yellow-800',
  OVERDUE: 'bg-red-100 text-red-800',
  PAID: 'bg-green-100 text-green-800',
  PARTIALLY_PAID: 'bg-orange-100 text-orange-800',
  CANCELLED: 'bg-gray-100 text-gray-800'
};

export interface InvoiceTemplateData {
  id: string;
  name: string;
  templateCompanyName: string;
  templateLogo?: string | null;
  logoBase64?: string | null;
  layoutType: 'modern' | 'classic';
  colorScheme: {
    primary: string;
    secondary: string;
    text: string;
    background: string;
  };
  fontFamily: string;
}